import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, GraduationCap, BookOpen, Award, ClipboardCheck, BellRing, ScanLine } from "lucide-react";

const COLORS = ["#1e6fd9", "#c9a961", "#0a2540", "#2f9e6e", "#e76f51"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(r => setData(r.data)).catch(() => {}); }, []);

  return (
    <AdminLayout title="Dashboard">
      {!data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[110px] rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-6" data-testid="admin-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-kpi-grid">
            <KpiCard testid="kpi-users" label="Total de usuários" value={data.kpis.total_users} hint={`${data.kpis.validated_users} validados`} accent="primary" />
            <KpiCard testid="kpi-leads" label="Inscrições" value={data.kpis.leads_total} hint={`${data.kpis.leads_new} novas`} accent="accent" />
            <KpiCard testid="kpi-checkins" label="Check-ins hoje" value={data.kpis.checkins_today} hint={`${data.kpis.checkouts_today} check-outs`} accent="success" />
            <KpiCard testid="kpi-certs" label="Certificados emitidos" value={data.kpis.certificates_issued} accent="gold" />
            <KpiCard testid="kpi-consultancies" label="Consultorias pendentes" value={data.kpis.consultancies_pending} hint={`${data.kpis.consultancies_total} no total`} accent="warn" />
            <KpiCard testid="kpi-kits" label="Kits VIP pendentes" value={data.kpis.kit_vip_pending} hint={`${data.kpis.kit_vip_total} no total`} accent="gold" />
            <KpiCard testid="kpi-materials" label="Materiais publicados" value={data.kpis.materials_count} accent="primary" />
            <KpiCard testid="kpi-avg-hours" label="Média de horas/aluno" value={`${data.kpis.avg_hours_per_user || 0}h`} accent="primary" />
            <KpiCard testid="kpi-courses" label="Cursos presenciais ativos" value={data.kpis.courses_active} accent="primary" />
            <KpiCard testid="kpi-free" label="Aulas Gratuitas" value={data.kpis.free_courses} accent="primary" />
            <KpiCard testid="kpi-conv" label="Taxa de conversão" value={`${data.kpis.conversion_rate}%`} hint={`${data.kpis.leads_converted} convertidos`} accent="success" />
            <KpiCard testid="kpi-notif" label="Notificações enviadas" value={data.kpis.notifications_sent} accent="primary" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="rounded-xl">
              <CardContent className="p-4">
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Check-ins (últimos 14 dias)</h3>
                <div className="h-64 mt-3" data-testid="admin-attendance-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.attendance_series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colA" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1e6fd9" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#1e6fd9" stopOpacity={0.04} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#eef2f6" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="checkins" stroke="#1e6fd9" fill="url(#colA)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-xl">
              <CardContent className="p-4">
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Distribuição por perfil</h3>
                <div className="h-64 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.by_profile.map(p => ({ name: p.profile, value: p.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88} label>
                        {data.by_profile.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                      </Pie>
                      <Legend />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="rounded-xl">
              <CardContent className="p-4">
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Top estados</h3>
                <div className="h-64 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_state}>
                      <CartesianGrid stroke="#eef2f6" vertical={false} />
                      <XAxis dataKey="estado" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0a2540" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-xl">
              <CardContent className="p-4">
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Top sugestões</h3>
                <ul className="mt-3 space-y-2">
                  {data.top_suggestions.length === 0 && <li className="text-sm text-muted-foreground">Nenhuma sugestão ainda.</li>}
                  {data.top_suggestions.map(s => (
                    <li key={s.id} className="flex items-center justify-between gap-2 border border-[hsl(var(--border))] rounded-lg p-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{s.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">{s.area}</p>
                      </div>
                      <span className="text-sm font-semibold text-[hsl(var(--accent))]">{s.votes} votos</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {data.by_level && data.by_level.length > 0 && (
            <Card className="rounded-xl">
              <CardContent className="p-4">
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Alunos por nível</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3" data-testid="dashboard-by-level">
                  {["Aluno CEAP", "Aluno Recorrente", "Especialista CEAP", "Embaixador CEAP"].map(lvl => {
                    const found = data.by_level.find(x => x.level === lvl);
                    const count = found?.count || 0;
                    const total = data.by_level.reduce((acc, x) => acc + (x.count || 0), 0) || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={lvl} className="rounded-xl border border-[hsl(var(--border))] p-3">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{lvl}</p>
                        <p className="font-display font-semibold text-2xl text-[hsl(var(--primary))] mt-1">{count}</p>
                        <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                          <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
